import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'es-toolkit';
import {
  convertAndRound,
  Decimal,
  differsBeyondTolerance,
  ONE,
  parseDecimal,
  roundToCurrencyPrecision,
  ZERO
} from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import { DEFAULT_DEBOUNCE_MS } from '../../../../hooks/use-debounce';

/** An invoice's paid state; the totals form shows it and never edits it. */
export type InvoiceTotalsPaymentStatus = 'PAID' | 'PARTIALLY_PAID' | 'UNPAID';

/** One boolean per accounting amount field. */
export interface AccountingAmountFlags {
  accountingAmount: boolean;
  accountingAmountNoVat: boolean;
  accountingTotalVatAmount: boolean;
}

export interface UseInvoiceTotalsFormStateOptions {
  open: boolean;
  currentTotalNoVat: Decimal;
  currentTotalVatAmount: Decimal;
  currentTotalInclVat: Decimal;
  currentAccountingExchangeRate?: Decimal | null;
  currentRoundingAmount?: Decimal | null;
  currentAccountingAmount?: Decimal | null;
  currentAccountingAmountNoVat?: Decimal | null;
  currentAccountingTotalVatAmount?: Decimal | null;
  /** Initial accounting currency selection — user-editable in the form. */
  currentAccountingCurrencyCode?: CurrencyCode | string | null;
  /** Document (invoice) currency. When it matches the accounting currency the
   * exchange rate is fixed to 1 and no conversion is performed. */
  documentCurrencyCode?: CurrencyCode | string | null;
}

export interface UseInvoiceTotalsFormStateResult {
  /** Display string for the "total without VAT" input. */
  noVatDisplay: string;
  /** Display string for the "total incl. VAT" input. */
  inclVatDisplay: string;
  /** Currently effective (parsed or committed) total without VAT. */
  effectiveNoVat: Decimal;
  /** Currently effective (parsed or committed) total incl. VAT. */
  effectiveInclVat: Decimal;
  /** Display string for the editable "VAT amount" input. */
  vatAmountDisplay: string;
  /** Currently effective (parsed or committed) total VAT amount. */
  vatAmount: Decimal;
  error: string | null;
  setError: (err: string | null) => void;
  setRawNoVat: (value: string) => void;
  setRawVatAmount: (value: string) => void;
  setRawInclVat: (value: string) => void;
  commitNoVat: () => void;
  commitVatAmount: () => void;
  commitInclVat: () => void;
  /** Set a total directly (e.g. filled from VAT summary / line items / expected). */
  setNoVatValue: (value: Decimal) => void;
  setVatAmountValue: (value: Decimal) => void;
  setInclVatValue: (value: Decimal) => void;
  /**
   * True once any total (or the rounding amount) changed in this form session.
   * Persisted mismatch warnings describe the saved totals, so they go stale then.
   */
  totalsTouched: boolean;
  /** Display string for the accounting exchange rate input. */
  accountingExchangeRateDisplay: string;
  /** Effective accounting exchange rate. null when blank. */
  effectiveAccountingExchangeRate: Decimal | null;
  setRawAccountingExchangeRate: (value: string) => void;
  commitAccountingExchangeRate: () => void;
  /** Set the rate directly (e.g. apply suggested rate) and recompute accounting amounts. */
  applyAccountingExchangeRate: (value: Decimal) => void;
  /** Display string for the rounding amount input. */
  roundingAmountDisplay: string;
  /** Effective rounding amount. null when blank. */
  effectiveRoundingAmount: Decimal | null;
  setRawRoundingAmount: (value: string) => void;
  commitRoundingAmount: () => void;
  /** Display string for the accounting amount without VAT input. */
  accountingAmountNoVatDisplay: string;
  effectiveAccountingAmountNoVat: Decimal | null;
  setRawAccountingAmountNoVat: (value: string) => void;
  commitAccountingAmountNoVat: () => void;
  /** Display string for the accounting total VAT amount input. */
  accountingTotalVatAmountDisplay: string;
  effectiveAccountingTotalVatAmount: Decimal | null;
  setRawAccountingTotalVatAmount: (value: string) => void;
  commitAccountingTotalVatAmount: () => void;
  /** Display string for the accounting total amount input. */
  accountingAmountDisplay: string;
  effectiveAccountingAmount: Decimal | null;
  setRawAccountingAmount: (value: string) => void;
  commitAccountingAmount: () => void;
  /** Currently selected accounting currency (null when unset). */
  accountingCurrencyCode: string | null;
  setAccountingCurrencyCode: (code: string | null) => void;
  /** True when the accounting currency equals the document currency. */
  currenciesSame: boolean;
  /** Recompute the 3 accounting amounts from the effective totals × exchange rate. */
  recomputeAccounting: () => void;
  /**
   * Expected accounting amounts derived from the current exchange rate × invoice totals.
   * Non-null whenever a rate is present and the currencies differ.
   */
  expectedAccountingAmounts: {
    accountingAmount: Decimal | null;
    accountingAmountNoVat: Decimal | null;
    accountingTotalVatAmount: Decimal | null;
  } | null;
  /**
   * Per-field flag: the entered accounting amount deviates from the expected one.
   * Use to render "Očekáváno" hints next to the accounting amount inputs.
   */
  accountingMismatches: AccountingAmountFlags;
  /** Flush any pending raw values and return the final form values. */
  flushAndGet: () => {
    finalNoVat: Decimal;
    finalVatAmount: Decimal;
    finalInclVat: Decimal;
    finalAccountingExchangeRate: Decimal | null;
    finalRoundingAmount: Decimal | null;
    finalAccountingAmount: Decimal | null;
    finalAccountingAmountNoVat: Decimal | null;
    finalAccountingTotalVatAmount: Decimal | null;
    finalAccountingCurrencyCode: string | null;
  };
}

const parseAmount = (value: string): Decimal => parseDecimal(value) ?? ZERO;

/** Parse to Decimal, returning null when input is blank (no implicit zero). */
const parseOptionalAmount = (value: string): Decimal | null => {
  if (value.trim() === '') return null;
  return parseDecimal(value) ?? null;
};

const displayOptional = (value: Decimal | null): string => (value === null ? '' : value.toString());

/** Rate + accounting currency fingerprint — identifies one conversion basis. */
const conversionKeyOf = (rate: Decimal | null, currencyCode: string | null): string =>
  `${rate?.toString() ?? ''}|${currencyCode ?? ''}`;

const noAccountingFlags = (): AccountingAmountFlags => ({
  accountingAmount: false,
  accountingAmountNoVat: false,
  accountingTotalVatAmount: false
});

interface WriteAccountingAmountsOptions {
  /**
   * Automatic run: leave accounting amounts the user entered by hand alone, and
   * convert from the committed totals rather than half-typed ones.
   */
  skipManualFields?: boolean;
}

export function useInvoiceTotalsFormState({
  open,
  currentTotalNoVat,
  currentTotalVatAmount,
  currentTotalInclVat,
  currentAccountingExchangeRate,
  currentRoundingAmount,
  currentAccountingAmount,
  currentAccountingAmountNoVat,
  currentAccountingTotalVatAmount,
  currentAccountingCurrencyCode,
  documentCurrencyCode
}: UseInvoiceTotalsFormStateOptions): UseInvoiceTotalsFormStateResult {
  const [totalNoVat, setTotalNoVat] = useState<Decimal>(ZERO);
  const [totalVatAmount, setTotalVatAmount] = useState<Decimal>(ZERO);
  const [totalInclVat, setTotalInclVat] = useState<Decimal>(ZERO);
  const [rawNoVat, setRawNoVat] = useState<string | null>(null);
  const [rawVatAmount, setRawVatAmount] = useState<string | null>(null);
  const [rawInclVat, setRawInclVat] = useState<string | null>(null);
  const [accountingExchangeRate, setAccountingExchangeRate] = useState<Decimal | null>(null);
  const [rawAccountingExchangeRate, setRawAccountingExchangeRate] = useState<string | null>(null);
  const [roundingAmount, setRoundingAmount] = useState<Decimal | null>(null);
  const [rawRoundingAmount, setRawRoundingAmount] = useState<string | null>(null);
  const [accountingAmount, setAccountingAmount] = useState<Decimal | null>(null);
  const [rawAccountingAmount, setRawAccountingAmount] = useState<string | null>(null);
  const [accountingAmountNoVat, setAccountingAmountNoVat] = useState<Decimal | null>(null);
  const [rawAccountingAmountNoVat, setRawAccountingAmountNoVat] = useState<string | null>(null);
  const [accountingTotalVatAmount, setAccountingTotalVatAmount] = useState<Decimal | null>(null);
  const [rawAccountingTotalVatAmount, setRawAccountingTotalVatAmount] = useState<string | null>(
    null
  );
  const [accountingCurrencyCode, setAccountingCurrencyCode] = useState<string | null>(null);
  const [totalsTouched, setTotalsTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wasOpen = useRef(false);
  // Conversion basis already reflected in the accounting amounts. Seeded on open
  // so simply opening the form never rewrites the stored amounts.
  const appliedConversionKey = useRef('');
  // Rate + accounting currency at the moment the form was opened. A rate belongs
  // to one currency pair, so only the opened pair's rate can be restored.
  const seedAccountingExchangeRate = useRef<Decimal | null>(null);
  const seedAccountingCurrencyCode = useRef<string | null>(null);
  // Accounting amounts the user entered by hand — the automatic conversion may
  // only overwrite what it wrote itself.
  const manualAccountingFields = useRef<AccountingAmountFlags>(noAccountingFlags());

  useEffect(() => {
    // Seed only on the closed→open transition. The current* props are fresh
    // Decimal instances each render; re-seeding on their identity loops forever.
    if (open && !wasOpen.current) {
      setTotalNoVat(currentTotalNoVat);
      setTotalVatAmount(currentTotalVatAmount);
      setTotalInclVat(currentTotalInclVat);
      setRawNoVat(null);
      setRawVatAmount(null);
      setRawInclVat(null);
      setAccountingExchangeRate(currentAccountingExchangeRate ?? null);
      setRawAccountingExchangeRate(null);
      appliedConversionKey.current = conversionKeyOf(
        currentAccountingExchangeRate ?? null,
        currentAccountingCurrencyCode ?? null
      );
      seedAccountingExchangeRate.current = currentAccountingExchangeRate ?? null;
      seedAccountingCurrencyCode.current = currentAccountingCurrencyCode ?? null;
      manualAccountingFields.current = noAccountingFlags();
      setRoundingAmount(currentRoundingAmount ?? null);
      setRawRoundingAmount(null);
      setAccountingAmount(currentAccountingAmount ?? null);
      setRawAccountingAmount(null);
      setAccountingAmountNoVat(currentAccountingAmountNoVat ?? null);
      setRawAccountingAmountNoVat(null);
      setAccountingTotalVatAmount(currentAccountingTotalVatAmount ?? null);
      setRawAccountingTotalVatAmount(null);
      setAccountingCurrencyCode(currentAccountingCurrencyCode ?? null);
      setTotalsTouched(false);
      setError(null);
    }
    wasOpen.current = open;
  }, [
    open,
    currentTotalNoVat,
    currentTotalVatAmount,
    currentTotalInclVat,
    currentAccountingExchangeRate,
    currentRoundingAmount,
    currentAccountingAmount,
    currentAccountingAmountNoVat,
    currentAccountingTotalVatAmount,
    currentAccountingCurrencyCode
  ]);

  // Same currency ⇒ no conversion: the rate is fixed to 1.
  const currenciesSame =
    documentCurrencyCode != null &&
    accountingCurrencyCode != null &&
    documentCurrencyCode === accountingCurrencyCode;

  const effectiveNoVat = rawNoVat !== null ? parseAmount(rawNoVat) : totalNoVat;
  const effectiveInclVat = rawInclVat !== null ? parseAmount(rawInclVat) : totalInclVat;
  const vatAmount = rawVatAmount !== null ? parseAmount(rawVatAmount) : totalVatAmount;
  const effectiveAccountingExchangeRate = currenciesSame
    ? ONE
    : rawAccountingExchangeRate !== null
      ? parseOptionalAmount(rawAccountingExchangeRate)
      : accountingExchangeRate;
  const effectiveRoundingAmount =
    rawRoundingAmount !== null ? parseOptionalAmount(rawRoundingAmount) : roundingAmount;
  const effectiveAccountingAmount =
    rawAccountingAmount !== null ? parseOptionalAmount(rawAccountingAmount) : accountingAmount;
  const effectiveAccountingAmountNoVat =
    rawAccountingAmountNoVat !== null
      ? parseOptionalAmount(rawAccountingAmountNoVat)
      : accountingAmountNoVat;
  const effectiveAccountingTotalVatAmount =
    rawAccountingTotalVatAmount !== null
      ? parseOptionalAmount(rawAccountingTotalVatAmount)
      : accountingTotalVatAmount;

  const commitNoVat = useCallback(() => {
    if (rawNoVat !== null) {
      setTotalNoVat(parseAmount(rawNoVat));
      setRawNoVat(null);
    }
  }, [rawNoVat]);

  const commitVatAmount = useCallback(() => {
    if (rawVatAmount !== null) {
      setTotalVatAmount(parseAmount(rawVatAmount));
      setRawVatAmount(null);
    }
  }, [rawVatAmount]);

  const commitInclVat = useCallback(() => {
    if (rawInclVat !== null) {
      setTotalInclVat(parseAmount(rawInclVat));
      setRawInclVat(null);
    }
  }, [rawInclVat]);

  const setNoVatValue = useCallback((value: Decimal) => {
    setTotalNoVat(value);
    setRawNoVat(null);
    setTotalsTouched(true);
  }, []);

  const setVatAmountValue = useCallback((value: Decimal) => {
    setTotalVatAmount(value);
    setRawVatAmount(null);
    setTotalsTouched(true);
  }, []);

  const setInclVatValue = useCallback((value: Decimal) => {
    setTotalInclVat(value);
    setRawInclVat(null);
    setTotalsTouched(true);
  }, []);

  const commitAccountingExchangeRate = useCallback(() => {
    if (rawAccountingExchangeRate !== null) {
      setAccountingExchangeRate(parseOptionalAmount(rawAccountingExchangeRate));
      setRawAccountingExchangeRate(null);
    }
  }, [rawAccountingExchangeRate]);

  // Convert the invoice totals into the accounting currency, overwriting the
  // three accounting amount fields.
  const writeAccountingAmounts = useCallback(
    (
      rate: Decimal,
      currencyCode: string | null,
      { skipManualFields = false }: WriteAccountingAmountsOptions = {}
    ) => {
      const ccy = (currencyCode ?? undefined) as CurrencyCode | undefined;
      // An automatic run may land mid-keystroke, so it converts the committed
      // totals; an explicit one converts what the user currently sees.
      const sourceNoVat = skipManualFields ? totalNoVat : effectiveNoVat;
      const sourceInclVat = skipManualFields ? totalInclVat : effectiveInclVat;
      const sourceVatAmount = skipManualFields ? totalVatAmount : vatAmount;
      const manual = manualAccountingFields.current;
      if (!skipManualFields || !manual.accountingAmountNoVat) {
        setAccountingAmountNoVat(convertAndRound(sourceNoVat, rate, ccy) ?? null);
        setRawAccountingAmountNoVat(null);
        manual.accountingAmountNoVat = false;
      }
      if (!skipManualFields || !manual.accountingAmount) {
        setAccountingAmount(convertAndRound(sourceInclVat, rate, ccy) ?? null);
        setRawAccountingAmount(null);
        manual.accountingAmount = false;
      }
      if (!skipManualFields || !manual.accountingTotalVatAmount) {
        setAccountingTotalVatAmount(convertAndRound(sourceVatAmount, rate, ccy) ?? null);
        setRawAccountingTotalVatAmount(null);
        manual.accountingTotalVatAmount = false;
      }
    },
    [totalNoVat, totalInclVat, totalVatAmount, effectiveNoVat, effectiveInclVat, vatAmount]
  );

  // The debounced writer below must not be re-created when the totals change —
  // only a rate/currency change may trigger an automatic conversion.
  const writeAccountingAmountsRef = useRef(writeAccountingAmounts);
  useEffect(() => {
    writeAccountingAmountsRef.current = writeAccountingAmounts;
  }, [writeAccountingAmounts]);

  const conversionKey = conversionKeyOf(effectiveAccountingExchangeRate, accountingCurrencyCode);

  const debouncedApplyConversion = useMemo(
    () =>
      debounce((rate: Decimal, currencyCode: string | null, key: string) => {
        writeAccountingAmountsRef.current(rate, currencyCode, { skipManualFields: true });
        appliedConversionKey.current = key;
      }, DEFAULT_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debouncedApplyConversion.cancel(), [debouncedApplyConversion]);

  // Auto-convert shortly after the rate (or accounting currency) changes, so the
  // accounting amounts follow without pressing "Recompute". Keyed on the rate's
  // string value: every keystroke builds a fresh Decimal instance.
  useEffect(() => {
    if (!open) {
      debouncedApplyConversion.cancel();
      return;
    }
    // Matching currencies fix the rate to 1, and a rate the user just erased must not
    // convert either — drop the pending run so a retype can schedule a fresh one.
    const rateErased =
      rawAccountingExchangeRate !== null && effectiveAccountingExchangeRate === null;
    if (currenciesSame || rateErased) {
      debouncedApplyConversion.cancel();
      appliedConversionKey.current = conversionKey;
      return;
    }
    // Back on the applied basis — a run scheduled for an abandoned intermediate
    // rate/currency may still be pending; it must not fire.
    if (conversionKey === appliedConversionKey.current) {
      debouncedApplyConversion.cancel();
      return;
    }
    // No rate at all — neither typed nor stored, so there is nothing to convert
    // with; drop anything still pending.
    if (effectiveAccountingExchangeRate === null) {
      debouncedApplyConversion.cancel();
      return;
    }
    debouncedApplyConversion(
      effectiveAccountingExchangeRate,
      accountingCurrencyCode,
      conversionKey
    );
  }, [
    open,
    currenciesSame,
    conversionKey,
    effectiveAccountingExchangeRate,
    rawAccountingExchangeRate,
    accountingCurrencyCode,
    debouncedApplyConversion
  ]);

  const applyAccountingExchangeRate = useCallback(
    (value: Decimal) => {
      setAccountingExchangeRate(value);
      setRawAccountingExchangeRate(null);
      if (currenciesSame) return;
      debouncedApplyConversion.cancel();
      writeAccountingAmounts(value, accountingCurrencyCode);
      appliedConversionKey.current = conversionKeyOf(value, accountingCurrencyCode);
    },
    [currenciesSame, accountingCurrencyCode, writeAccountingAmounts, debouncedApplyConversion]
  );

  // Switching the accounting currency invalidates the rate — it was quoted for
  // the previous pair. Cleared so nothing converts on a stale basis; returning to
  // the opened currency restores the rate stored for it.
  const changeAccountingCurrencyCode = useCallback(
    (code: string | null) => {
      if (code === accountingCurrencyCode) return;
      debouncedApplyConversion.cancel();
      setAccountingCurrencyCode(code);
      setAccountingExchangeRate(
        code === seedAccountingCurrencyCode.current ? seedAccountingExchangeRate.current : null
      );
      setRawAccountingExchangeRate(null);
    },
    [accountingCurrencyCode, debouncedApplyConversion]
  );

  const commitRoundingAmount = useCallback(() => {
    if (rawRoundingAmount !== null) {
      setRoundingAmount(parseOptionalAmount(rawRoundingAmount));
      setRawRoundingAmount(null);
    }
  }, [rawRoundingAmount]);

  const commitAccountingAmount = useCallback(() => {
    if (rawAccountingAmount !== null) {
      setAccountingAmount(parseOptionalAmount(rawAccountingAmount));
      setRawAccountingAmount(null);
    }
  }, [rawAccountingAmount]);

  const commitAccountingAmountNoVat = useCallback(() => {
    if (rawAccountingAmountNoVat !== null) {
      setAccountingAmountNoVat(parseOptionalAmount(rawAccountingAmountNoVat));
      setRawAccountingAmountNoVat(null);
    }
  }, [rawAccountingAmountNoVat]);

  const commitAccountingTotalVatAmount = useCallback(() => {
    if (rawAccountingTotalVatAmount !== null) {
      setAccountingTotalVatAmount(parseOptionalAmount(rawAccountingTotalVatAmount));
      setRawAccountingTotalVatAmount(null);
    }
  }, [rawAccountingTotalVatAmount]);

  // Recompute the accounting amounts from effective totals × exchange rate,
  // mirroring the backend's calculateAccountingTotals. No-op without a rate.
  const recomputeAccounting = useCallback(() => {
    if (currenciesSame) return;
    const rate = effectiveAccountingExchangeRate;
    if (rate === null) return;
    debouncedApplyConversion.cancel();
    writeAccountingAmounts(rate, accountingCurrencyCode);
    appliedConversionKey.current = conversionKeyOf(rate, accountingCurrencyCode);
  }, [
    currenciesSame,
    effectiveAccountingExchangeRate,
    accountingCurrencyCode,
    writeAccountingAmounts,
    debouncedApplyConversion
  ]);

  // Expected accounting amounts from current rate × invoice totals. Non-null
  // whenever a rate is present and the currencies differ.
  const expectedAccountingAmounts = useMemo(() => {
    if (currenciesSame || effectiveAccountingExchangeRate === null) return null;
    const rate = effectiveAccountingExchangeRate;
    const ccy = (accountingCurrencyCode ?? undefined) as CurrencyCode | undefined;
    return {
      accountingAmount: convertAndRound(effectiveInclVat, rate, ccy) ?? null,
      accountingAmountNoVat: convertAndRound(effectiveNoVat, rate, ccy) ?? null,
      accountingTotalVatAmount: convertAndRound(vatAmount, rate, ccy) ?? null
    };
  }, [
    effectiveAccountingExchangeRate,
    accountingCurrencyCode,
    currenciesSame,
    effectiveInclVat,
    effectiveNoVat,
    vatAmount
  ]);

  // Which accounting amounts deviate from the expected conversion — drives the
  // "Očekáváno" hints, so they only appear on a real difference.
  const accountingMismatches = useMemo(() => {
    const expected = expectedAccountingAmounts;
    if (expected === null) return noAccountingFlags();
    return {
      accountingAmount: differsBeyondTolerance(
        effectiveAccountingAmount,
        expected.accountingAmount
      ),
      accountingAmountNoVat: differsBeyondTolerance(
        effectiveAccountingAmountNoVat,
        expected.accountingAmountNoVat
      ),
      accountingTotalVatAmount: differsBeyondTolerance(
        effectiveAccountingTotalVatAmount,
        expected.accountingTotalVatAmount
      )
    };
  }, [
    expectedAccountingAmounts,
    effectiveAccountingAmount,
    effectiveAccountingAmountNoVat,
    effectiveAccountingTotalVatAmount
  ]);

  const flushAndGet = useCallback(() => {
    const finalNoVat = rawNoVat !== null ? parseAmount(rawNoVat) : totalNoVat;
    const finalVatAmount = rawVatAmount !== null ? parseAmount(rawVatAmount) : totalVatAmount;
    const finalInclVat = rawInclVat !== null ? parseAmount(rawInclVat) : totalInclVat;
    // The exchange rate is exempt from currency rounding — it keeps its full
    // precision (up to 4 decimals), unlike the amounts below.
    const finalAccountingExchangeRate = currenciesSame
      ? ONE
      : rawAccountingExchangeRate !== null
        ? parseOptionalAmount(rawAccountingExchangeRate)
        : accountingExchangeRate;
    const finalRoundingAmount =
      rawRoundingAmount !== null ? parseOptionalAmount(rawRoundingAmount) : roundingAmount;
    // Accounting amounts are rounded to the accounting currency precision so the
    // saved value matches what's displayed, regardless of entry path (seeded,
    // recomputed, or typed).
    const accountingCcy = (accountingCurrencyCode ?? undefined) as CurrencyCode | undefined;
    const roundAccounting = (value: Decimal | null): Decimal | null =>
      value === null ? null : roundToCurrencyPrecision(value, accountingCcy);
    // A rate typed moments ago may still have its debounced conversion pending —
    // convert here as well so saving right away can't persist stale amounts.
    const pendingRate =
      !currenciesSame &&
      finalAccountingExchangeRate !== null &&
      conversionKeyOf(finalAccountingExchangeRate, accountingCurrencyCode) !==
        appliedConversionKey.current
        ? finalAccountingExchangeRate
        : null;
    // A hand-entered amount wins over the pending conversion — same guard as the
    // debounced writer, so blurring the field can't turn it into a converted one.
    const manual = manualAccountingFields.current;
    const resolveAccounting = (
      raw: string | null,
      committed: Decimal | null,
      sourceTotal: Decimal,
      manuallyEdited: boolean
    ): Decimal | null =>
      pendingRate !== null && !manuallyEdited
        ? (convertAndRound(sourceTotal, pendingRate, accountingCcy) ?? null)
        : roundAccounting(raw !== null ? parseOptionalAmount(raw) : committed);
    const finalAccountingAmount = resolveAccounting(
      rawAccountingAmount,
      accountingAmount,
      finalInclVat,
      manual.accountingAmount
    );
    const finalAccountingAmountNoVat = resolveAccounting(
      rawAccountingAmountNoVat,
      accountingAmountNoVat,
      finalNoVat,
      manual.accountingAmountNoVat
    );
    const finalAccountingTotalVatAmount = resolveAccounting(
      rawAccountingTotalVatAmount,
      accountingTotalVatAmount,
      finalVatAmount,
      manual.accountingTotalVatAmount
    );
    return {
      finalNoVat,
      finalVatAmount,
      finalInclVat,
      finalAccountingExchangeRate,
      finalRoundingAmount,
      finalAccountingAmount,
      finalAccountingAmountNoVat,
      finalAccountingTotalVatAmount,
      finalAccountingCurrencyCode: accountingCurrencyCode
    };
  }, [
    currenciesSame,
    rawNoVat,
    rawVatAmount,
    rawInclVat,
    totalNoVat,
    totalVatAmount,
    totalInclVat,
    rawAccountingExchangeRate,
    accountingExchangeRate,
    rawRoundingAmount,
    roundingAmount,
    rawAccountingAmount,
    accountingAmount,
    rawAccountingAmountNoVat,
    accountingAmountNoVat,
    rawAccountingTotalVatAmount,
    accountingTotalVatAmount,
    accountingCurrencyCode
  ]);

  return {
    noVatDisplay: rawNoVat ?? totalNoVat.toString(),
    inclVatDisplay: rawInclVat ?? totalInclVat.toString(),
    vatAmountDisplay: rawVatAmount ?? totalVatAmount.toString(),
    effectiveNoVat,
    effectiveInclVat,
    vatAmount,
    error,
    setError,
    setRawNoVat: value => {
      setRawNoVat(value);
      setTotalsTouched(true);
    },
    setRawVatAmount: value => {
      setRawVatAmount(value);
      setTotalsTouched(true);
    },
    setRawInclVat: value => {
      setRawInclVat(value);
      setTotalsTouched(true);
    },
    commitNoVat,
    commitVatAmount,
    commitInclVat,
    setNoVatValue,
    setVatAmountValue,
    setInclVatValue,
    totalsTouched,
    accountingExchangeRateDisplay: currenciesSame
      ? ONE.toString()
      : (rawAccountingExchangeRate ?? displayOptional(accountingExchangeRate)),
    effectiveAccountingExchangeRate,
    setRawAccountingExchangeRate: value => setRawAccountingExchangeRate(value),
    commitAccountingExchangeRate,
    applyAccountingExchangeRate,
    roundingAmountDisplay: rawRoundingAmount ?? displayOptional(roundingAmount),
    effectiveRoundingAmount,
    setRawRoundingAmount: value => {
      setRawRoundingAmount(value);
      setTotalsTouched(true);
    },
    commitRoundingAmount,
    accountingAmountNoVatDisplay:
      rawAccountingAmountNoVat ?? displayOptional(accountingAmountNoVat),
    effectiveAccountingAmountNoVat,
    setRawAccountingAmountNoVat: value => {
      manualAccountingFields.current.accountingAmountNoVat = true;
      setRawAccountingAmountNoVat(value);
    },
    commitAccountingAmountNoVat,
    accountingTotalVatAmountDisplay:
      rawAccountingTotalVatAmount ?? displayOptional(accountingTotalVatAmount),
    effectiveAccountingTotalVatAmount,
    setRawAccountingTotalVatAmount: value => {
      manualAccountingFields.current.accountingTotalVatAmount = true;
      setRawAccountingTotalVatAmount(value);
    },
    commitAccountingTotalVatAmount,
    accountingAmountDisplay: rawAccountingAmount ?? displayOptional(accountingAmount),
    effectiveAccountingAmount,
    setRawAccountingAmount: value => {
      manualAccountingFields.current.accountingAmount = true;
      setRawAccountingAmount(value);
    },
    commitAccountingAmount,
    accountingCurrencyCode,
    setAccountingCurrencyCode: changeAccountingCurrencyCode,
    currenciesSame,
    recomputeAccounting,
    expectedAccountingAmounts,
    accountingMismatches,
    flushAndGet
  };
}
